import { VoucherType } from "@tm/models"
import { useLocalization } from "@tm/localization"
import { Box, Divider, Typography, Paper, Loader, Stack } from "@tm/components"
import { voucherListFilterAtom } from "@bundles/vouchers/data/states/listFilters"
import { useRecoilState } from "recoil"
import { isAfter, isBefore } from "date-fns"
import VoucherHeader from "../_shared/VoucherDetailsHeader"
import OfferDetails from "./components/OfferDetails"
import { CustomerOverview } from "./components/CustomerOverview"
import { useCostEstimationDetails } from "../../../../business/hooks/useCostEstimationDetails"
import Parts from "./components/parts"
import Works from "./components/works"
import CostEstimationTotalNumbers from "./components/CostEstimationTotalNumbers"
import VehicleImage from "./components/VehicleImage"
import OfferNote from "./components/OfferNote"
import { AllTotals } from "./components/totals/AllTotals"

type Props = {
    workTaskId: string
    voucherId: string | undefined
    isBigScreen: boolean
}

export function CostEstimationDetails({ voucherId, isBigScreen, workTaskId }: Props) {
    const { translateText } = useLocalization()

    const { costEstimationDetails, costEstimationDetailsLoading, toggleSelected, selectAll, unselectAll, selectedIds } =
        useCostEstimationDetails(voucherId)

    const [listFilters, setListFilter] = useRecoilState(voucherListFilterAtom(workTaskId))

    if (costEstimationDetailsLoading) {
        return <Loader />
    }
    if (!costEstimationDetails) {
        return <Typography>{translateText(323)}</Typography>
    }

    const { totalNumbers, costEstimation, vehicle, creationDate, voucherNumber, customer, repairShopLogo } = costEstimationDetails
    const { details, currencyCode, currencySymbol, totalsB2B, totalsB2C, costEstimationOptions } = costEstimation || {}
    const { memo, validityPeriod, workSchedule } = details || {}
    const { priceTypeMode, customerGroupMode } = costEstimationOptions || {}

    if (isAfter(creationDate, listFilters.startDate) || isBefore(creationDate, listFilters.endDate)) {
        setListFilter((prev) => ({
            ...prev,
            startDate: creationDate,
            endDate: creationDate,
        }))
    }

    let totalPurchasePrice
    let totalRetailPrice
    // Purchase
    if (priceTypeMode) {
        totalPurchasePrice = customerGroupMode ? totalsB2B?.offerPrice : totalsB2C?.offerPriceVatIncluded
    } else {
        totalRetailPrice = customerGroupMode ? totalsB2B?.offerPrice : totalsB2C?.offerPriceVatIncluded
    }

    return (
        <Stack spacing={2} divider={<Divider light />}>
            <Stack spacing={1}>
                <VoucherHeader
                    currencyCode={currencyCode}
                    currencySymbol={currencySymbol}
                    orderDate={creationDate}
                    voucherNumber={voucherNumber}
                    totalPurchasePrice={totalPurchasePrice}
                    totalRetailPrice={totalRetailPrice}
                    type={VoucherType.CostEstimation}
                />
                <CustomerOverview customer={customer} vehicle={vehicle} repairShopLogo={repairShopLogo} />
            </Stack>
            <Paper>
                <Stack gap={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h3">{vehicle?.displayName || translateText(99)}</Typography>
                        <CostEstimationTotalNumbers
                            partsPositionCount={totalNumbers?.partsPositionCount}
                            worksPositionCount={totalNumbers?.worksPositionCount}
                        />
                    </Box>
                    <Box px={2} pb={2}>
                        {vehicle?.vehicleImage && <VehicleImage base64Image={vehicle.vehicleImage} />}
                        <Parts
                            workTaskId={workTaskId}
                            costEstimation={costEstimation}
                            voucherId={voucherId}
                            selectedIds={selectedIds}
                            vehicleId={vehicle?.id}
                            isBigScreen={isBigScreen}
                            onSelectAll={selectAll}
                            onSelectPart={toggleSelected}
                            onUnselectAll={unselectAll}
                        />
                        <Works costEstimation={costEstimation} />
                    </Box>
                </Stack>
            </Paper>
            <AllTotals costEstimation={costEstimation} />
            <OfferDetails workSchedule={workSchedule} validityPeriod={validityPeriod} />
            <OfferNote note={memo} />
        </Stack>
    )
}
